/**
 * featureFlags.ts
 *
 * This file maps client feature flags for the front-end.
 *
 * Feature flags can be opted in for client use by mapping them to
 * an enum value in ClientFeatureFlag.cs. The feature flag export here
 * should check if the clientFeatureFlags array includes the exact name
 * of the middle-tier ClientFeatureFlag enum value (PascalCase).
 *
 * These feature flags are transmitted to the client via JWT claims cookie
 * (ff claim). The cookies token value is base64 decoded on the client to
 * attain the feature flags synchronously.
 *
 * When running development build feature flags state can be queried via
 * chrome developer console by executing `featureFlags` (global).
 */ function _classCallCheck(instance, Constructor) {
    if (!(instance instanceof Constructor)) {
        throw new TypeError("Cannot call a class as a function");
    }
}
function _defineProperties(target, props) {
    for(var i = 0; i < props.length; i++){
        var descriptor = props[i];
        descriptor.enumerable = descriptor.enumerable || false;
        descriptor.configurable = true;
        if ("value" in descriptor) descriptor.writable = true;
        Object.defineProperty(target, descriptor.key, descriptor);
    }
}
function _createClass(Constructor, protoProps, staticProps) {
    if (protoProps) _defineProperties(Constructor.prototype, protoProps);
    if (staticProps) _defineProperties(Constructor, staticProps);
    return Constructor;
}
import { getSessionClaims } from "./sessionClaims";
// (b/153007582): This will only work for routes receiving the JWT.
// When the feature above is implemented, this can be used to detect
// ClientFeatureFlag across Kaggle.
var flagsCache;
var getFeatureFlags = function() {
    if (!flagsCache) {
        var ref;
        flagsCache = ((ref = getSessionClaims()) === null || ref === void 0 ? void 0 : ref.ff) || [];
    }
    return flagsCache;
};
var flagDetailsCache;
var getFeatureFlagsDetails = function() {
    if (!flagDetailsCache) {
        var ref;
        flagDetailsCache = ((ref = getSessionClaims()) === null || ref === void 0 ? void 0 : ref.ffd) || {};
    }
    return flagDetailsCache;
};
var getFeatureFlagValue = function(flagName) {
    return getFeatureFlagsDetails()[flagName];
};
// Using 'class' for getters.
var FeatureFlags = /*#__PURE__*/ function() {
    "use strict";
    function FeatureFlags() {
        _classCallCheck(this, FeatureFlags);
    }
    _createClass(FeatureFlags, [
        {
            key: "gcloudKernelInteg",
            get: /** The gcloud-kernel-ui FF. */ function get() {
                return getFeatureFlags().includes("GcloudKernelInteg");
            }
        },
        {
            key: "dockerModalSelector",
            get: /** Shows the new Docker Modal selector */ function get() {
                return getFeatureFlags().includes("DockerModalSelector");
            }
        },
        {
            key: "kernelsFirebaseProxy",
            get: /** The kernels-firebase-proxy FF. */ function get() {
                return getFeatureFlags().includes("KernelsFirebaseProxy");
            }
        },
        {
            key: "kernelsGCSUploadProxy",
            get: /** The kernels-gcs-upload-proxy FF. */ function get() {
                return getFeatureFlags().includes("KernelsGCSUploadProxy");
            }
        },
        {
            key: "kernelsFirebaseLongPolling",
            get: /** The kernels-firebase-long-polling FF. */ function get() {
                return getFeatureFlags().includes("KernelsFirebaseLongPolling");
            }
        },
        {
            key: "kernelsFirestoreDebugLogging",
            get: /** The kernels-firestore-debug-logging FF. */ function get() {
                return getFeatureFlags().includes("KernelsFirestoreDebugLogging");
            }
        },
        {
            key: "kernelsDraftUploadBlob",
            get: function get() {
                return getFeatureFlags().includes("KernelsDraftUploadBlob");
            }
        },
        {
            key: "kernelsExportMetadata",
            get: function get() {
                return getFeatureFlags().includes("KernelsExportMetadata");
            }
        },
        {
            key: "kernelEditorAutosaveThrottleMs",
            get: function get() {
                return getFeatureFlagValue("KernelEditorAutosaveThrottleMs");
            }
        },
        {
            key: "avatarProfilePreview",
            get: function get() {
                return getFeatureFlags().includes("AvatarProfilePreview");
            }
        },
        {
            key: "kernelsSnippetEditor",
            get: function get() {
                return getFeatureFlags().includes("KernelsSnippetEditor");
            }
        },
        {
            key: "communityLowerHeaderSizes",
            get: /** to use the new lower header sizes in our UserContent markdown renderer */ function get() {
                return getFeatureFlags().includes("CommunityLowerHeaderSizes");
            }
        },
        {
            key: "competitionPrivateDatabundle",
            get: /** Enable viewing and uploading a new private databundle from the competition host page. */ function get() {
                return getFeatureFlags().includes("CompetitionPrivateDatabundle");
            }
        },
        {
            key: "privateCompetitions",
            get: /** Enable creating private community competitions. */ function get() {
                return getFeatureFlags().includes("CompetitionsPrivateCompetitions");
            }
        },
        {
            key: "competitionsDelete",
            get: /** Enable deleting unlauched competitions. */ function get() {
                return getFeatureFlags().includes("CompetitionsDelete");
            }
        },
        {
            key: "competitionLicenses",
            get: /**
   * Enables Licenses for Competitions. See design doc for context:
   *  https://docs.google.com/document/d/1zM8jbF-k-wdtW3Y_eNMxGokLaGWU15wKWPFdxVadEZ4/edit#
   */ function get() {
                return getFeatureFlags().includes("CompetitionLicenses");
            }
        },
        {
            key: "tpuCommitScheduling",
            get: /** Enables the TPU commit scheduling. */ function get() {
                return getFeatureFlags().includes("TPUCommitScheduling");
            }
        },
        {
            key: "commitScheduling",
            get: /** Enables commit scheduling. */ function get() {
                return getFeatureFlags().includes("CommitScheduling");
            }
        },
        {
            key: "allowForumAttachments",
            get: /**
   * Allows forum attachments. When disabled, a pop-up message is shown
   * when the attachment button is visible and clicked.
   */ function get() {
                return getFeatureFlags().includes("AllowForumAttachments");
            }
        },
        {
            key: "kernelsSaveCellOutput",
            get: /** KernelsSaveCellOutput */ function get() {
                return getFeatureFlags().includes("KernelsSaveCellOutput");
            }
        },
        {
            key: "kernelEditorAddOnsPanel",
            get: /**
   * Enables the Add-ons panel for the Kernels Editor. b/268365596
   */ function get() {
                return getFeatureFlags().includes("KernelEditorAddOnsPanel");
            }
        },
        {
            key: "frontendErrorReporting",
            get: /** Enable frontend JS error reporting to Stackdriver. */ function get() {
                return getFeatureFlags().includes("FrontendErrorReporting");
            }
        },
        {
            key: "frontendErrorReportingSampleRate",
            get: /**
   * A string containing a number between 0 and 1, which represents the rate
   * we should report unhandled frontend errors. ex: "0.1" means we should
   * report 10% of the errors we observe on frontend clients.
   * Requires FrontendErrorReporting to have any functionality.
   */ function get() {
                return getFeatureFlagValue("FrontendErrorReportingSampleRate");
            }
        },
        {
            key: "registrationNewsEmailSignupIsOptOut",
            get: function get() {
                return getFeatureFlags().includes("RegistrationNewsEmailSignupIsOptOut");
            }
        },
        {
            key: "phoneVerifyForComment",
            get: function get() {
                return getFeatureFlags().includes("PhoneVerifyForComments");
            }
        },
        {
            key: "phoneVerifyForNewTopic",
            get: function get() {
                return getFeatureFlags().includes("PhoneVerifyForNewTopic");
            }
        },
        {
            key: "emergencyAlertBanner",
            get: function get() {
                return getFeatureFlagValue("EmergencyAlertBanner");
            }
        },
        {
            key: "clientRpcRateLimit",
            get: function get() {
                return getFeatureFlagValue("ClientRpcRateLimit");
            }
        },
        {
            key: "discussionsSimpleSorts",
            get: function get() {
                return getFeatureFlags().includes("DiscussionsSimpleSorts");
            }
        },
        {
            key: "featuredCommunityCompetitions",
            get: /**
   * Allows admins to feature community competitions.
   */ function get() {
                return getFeatureFlagValue("FeaturedCommunityCompetitions");
            }
        },
        {
            key: "addFeatureFlagsToPageLoadTag",
            get: function get() {
                return getFeatureFlagValue("AddFeatureFlagsToPageLoadTag");
            }
        },
        {
            key: "captchaDisabled",
            get: function get() {
                return getFeatureFlags().includes("CaptchaDisabled");
            }
        },
        {
            key: "competitionMetricTimeoutMinutes",
            get: function get() {
                return getFeatureFlagValue("CompetitionMetricTimeoutMinutes");
            }
        },
        {
            key: "kmDatasetUploader",
            get: /**
   * Enables the KM DatasetUploaderPanel for datasets landing page. b/266043694
   */ function get() {
                return getFeatureFlags().includes("KMDatasetUploader");
            }
        },
        {
            key: "kmDatasetVersionUploader",
            get: /**
   * Enables the KM DatasetVersionUploaderPanel for datasets detail pages.
   * b/292166951
   */ function get() {
                return getFeatureFlags().includes("KMDatasetVersionUploader");
            }
        },
        {
            key: "kmDatasetUploaderForKernels",
            get: /**
   * Enables the KM DatasetUploaderPanel for the kernel viewer and editor.
   * b/294433474
   */ function get() {
                return getFeatureFlags().includes("KMDatasetUploaderForKernels");
            }
        },
        {
            key: "kmCompetitionDataUploader",
            get: /** Enables the KM CompetitionDataUploaderPanel. b/286617947 */ function get() {
                return getFeatureFlags().includes("KMCompetitionDataUploader");
            }
        },
        {
            key: "datasetsManagedFocusOnOpen",
            get: function get() {
                return getFeatureFlags().includes("DatasetsManagedFocusOnOpen");
            }
        },
        {
            key: "doiDatasetTombstones",
            get: function get() {
                return getFeatureFlags().includes("DoiDatasetTombstones");
            }
        },
        {
            key: "changeDatasetOwnershipToOrg",
            get: function get() {
                return getFeatureFlags().includes("ChangeDatasetOwnershipToOrg");
            }
        },
        {
            key: "exportDatasetAsCroissant",
            get: function get() {
                return getFeatureFlags().includes("ExportDatasetAsCroissant");
            }
        },
        {
            key: "kmUserProfile",
            get: function get() {
                return getFeatureFlags().includes("KMUserProfile");
            }
        },
        {
            key: "mauReport",
            get: function get() {
                return getFeatureFlags().includes("MauReport");
            }
        },
        {
            key: "competitionsHostKm",
            get: function get() {
                return getFeatureFlags().includes("CompetitionsHostKm");
            }
        },
        {
            key: "modelsLandingPageCommunitySectionEnabled",
            get: function get() {
                return getFeatureFlags().includes("ModelsLandingPageCommunitySectionEnabled");
            }
        },
        {
            key: "modelsCachedTagServiceEnabled",
            get: function get() {
                return getFeatureFlags().includes("ModelsCachedTagServiceEnabled");
            }
        },
        {
            key: "modelsPublishing",
            get: function get() {
                return getFeatureFlags().includes("ModelsPublishing");
            }
        },
        {
            key: "modelsPublishingUi",
            get: function get() {
                return getFeatureFlags().includes("ModelsPublishing") && getFeatureFlags().includes("ModelsPublishingUi");
            }
        },
        {
            key: "modelsTabOnCompetitionPage",
            get: function get() {
                return getFeatureFlags().includes("ModelsTabOnCompetitionPage");
            }
        },
        {
            key: "competitionsFilesDropKaggleApi",
            get: function get() {
                return getFeatureFlags().includes("CompetitionsFilesDropKaggleApi");
            }
        },
        {
            key: "competitionsRulesKm",
            get: function get() {
                return getFeatureFlags().includes("CompetitionsRulesKm");
            }
        },
        {
            key: "competitionsOverviewKm",
            get: function get() {
                return getFeatureFlags().includes("CompetitionsOverviewKm");
            }
        },
        {
            key: "datasetsSharedWithThemSearch",
            get: function get() {
                return getFeatureFlags().includes("DatasetsSharedWithThemSearch");
            }
        },
        {
            key: "datasetsVotingChips",
            get: function get() {
                return getFeatureFlags().includes("DatasetsVotingChips");
            }
        },
        {
            key: "datasetsFeedbacksButtonNudge",
            get: function get() {
                return getFeatureFlags().includes("DatasetsFeedbacksButtonNudge");
            }
        },
        {
            key: "datasetsFeedbacksDelayNudge",
            get: function get() {
                return getFeatureFlags().includes("DatasetsFeedbacksDelayNudge");
            }
        },
        {
            key: "cmsKMUserContent",
            get: /**
   * Adds support for rendering CMS pages via KM UserContent with a special
   * HTML comment: "<!-- Enable KM UserContent -->". b/294257741
   */ function get() {
                return getFeatureFlags().includes("CmsKMUserContent");
            }
        },
        {
            key: "modelInstanceRenderedUsage",
            get: function get() {
                return getFeatureFlags().includes("ModelInstanceRenderedUsage");
            }
        },
        {
            key: "recentlyViewedModelsShelf",
            get: function get() {
                return getFeatureFlags().includes("RecentlyViewedModelsShelf");
            }
        },
        {
            key: "competitionsShowMetricListPopularity",
            get: function get() {
                return getFeatureFlags().includes("CompetitionsShowMetricListPopularity");
            }
        },
        {
            key: "yourWork",
            get: function get() {
                return getFeatureFlags().includes("YourWork");
            }
        },
        {
            key: "datasetsNewDeletedView",
            get: function get() {
                return getFeatureFlags().includes("DatasetsNewDeletedView");
            }
        },
        {
            key: "modelsOpenVertex",
            get: function get() {
                return getFeatureFlags().includes("ModelsOpenVertex");
            }
        },
        {
            key: "kmContact",
            get: function get() {
                return getFeatureFlags().includes("KMContact");
            }
        },
        {
            key: "kernelsA100GPUs",
            get: /** Enables A100 GPUs as an option for Kaggle Notebooks. http://b/296282655 */ function get() {
                return getFeatureFlags().includes("KernelsA100GPUs");
            }
        },
        {
            key: "kmSearch",
            get: function get() {
                return getFeatureFlags().includes("KmSearch");
            }
        },
        {
            key: "organizationsV2",
            get: function get() {
                return getFeatureFlags().includes("OrganizationsV2");
            }
        }
    ]);
    return FeatureFlags;
}();
export var featureFlags = new FeatureFlags();
// Allow feature flag inspection via console in dev.
if (process.env.NODE_ENV === "development") {
    window.featureFlags = featureFlags;
}
